import { useEffect, useState } from "react";
import { productApi } from "../api/productApi";
import AdminSidebar from "../components/AdminSidebar";
import Message from "../components/Message";

const emptyForm = {
  name: "",
  type: "",
  price: "",
  stock: "",
  image: "",
  description: "",
};

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [fileName, setFileName] = useState("");
  const [notice, setNotice] = useState(null);

  useEffect(() => {
    loadProducts();
  }, []);

  async function loadProducts() {
    try {
      const response = await productApi.getProducts({ page: 1, perPage: 100 });
      setProducts(response.items || []);
    } catch (error) {
      setNotice({ type: "error", text: error.message });
    }
  }

  function resetForm() {
    setForm(emptyForm);
    setEditingId(null);
    setFileName("");
  }

  function startEdit(product) {
    setEditingId(product.id);
    setForm({
      name: product.name || "",
      type: product.type || "",
      price: product.price ?? "",
      stock: product.stock ?? "",
      image: product.image || "",
      description: product.description || "",
    });
    setFileName(product.image ? "Existing image" : "");
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  async function handleFileChange(event) {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setNotice({ type: "info", text: "Uploading image..." });
      const data = await productApi.uploadImage(file);
      setForm((current) => ({ ...current, image: data.image_url }));
      setFileName(file.name);
      setNotice({ type: "success", text: "Image uploaded successfully." });
    } catch (error) {
      setNotice({ type: "error", text: error.message });
    }
  }

  async function handleSubmit(event) {
    event.preventDefault();

    const payload = {
      name: form.name.trim(),
      type: form.type.trim(),
      price: parseFloat(form.price),
      stock: parseInt(form.stock, 10),
      image: form.image,
      description: form.description.trim(),
    };

    try {
      if (editingId) {
        await productApi.updateProduct(editingId, payload);
        setNotice({ type: "success", text: "Device updated successfully." });
      } else {
        await productApi.addProduct(payload);
        setNotice({ type: "success", text: "Device added successfully." });
      }
      resetForm();
      loadProducts();
    } catch (error) {
      setNotice({ type: "error", text: error.message });
    }
  }

  async function handleDelete(product) {
    const confirmed = window.confirm(`Delete "${product.name}" from the catalogue?`);
    if (!confirmed) return;

    try {
      await productApi.deleteProduct(product.id);
      setNotice({ type: "success", text: "Device removed from catalogue." });
      if (editingId === product.id) resetForm();
      loadProducts();
    } catch (error) {
      setNotice({ type: "error", text: error.message });
    }
  }

  return (
    <div className="admin-container">
      <AdminSidebar />
      <div className="admin-content">
        <div className="admin-header">
          <h1>Device Catalogue Management</h1>
        </div>
        <Message notice={notice} />

        <div className="admin-section">
          <h2>{editingId ? "Edit Device" : "Add New Device"}</h2>
          <form onSubmit={handleSubmit} className="admin-form">
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="product_name">Device Name *</label>
                <input type="text" id="product_name" required className="form-control" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </div>
              <div className="form-group">
                <label htmlFor="product_type">Device Type *</label>
                <input type="text" id="product_type" required className="form-control" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="product_price">Unit Price *</label>
                <input type="number" id="product_price" step="0.01" min="0" required className="form-control" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} />
              </div>
              <div className="form-group">
                <label htmlFor="product_stock">Stock Quantity *</label>
                <input type="number" id="product_stock" min="0" required className="form-control" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="product_image_file">Upload Product Image</label>
              <div className="image-upload-group">
                <input type="file" id="product_image_file" accept="image/*" className="form-control" onChange={handleFileChange} />
                <small className="text-muted">Optional. Supported formats: PNG, JPG, JPEG, GIF, WebP.</small>
              </div>
            </div>

            {form.image ? (
              <div id="imagePreviewContainer" className="image-preview">
                <label>Image Preview:</label>
                <img id="imagePreview" src={form.image} alt="Preview" className="preview-image" />
                <p id="imageFileName" className="image-filename">{fileName || form.image}</p>
              </div>
            ) : null}

            <div className="form-group">
              <label htmlFor="product_description">Product Description</label>
              <textarea id="product_description" className="form-control" rows="4" placeholder="Enter product description..." value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}></textarea>
            </div>

            <button type="submit" className="btn btn-primary">{editingId ? "Update Device" : "Add Device"}</button>
            {editingId ? (
              <button type="button" className="btn btn-secondary" onClick={resetForm} style={{ marginLeft: 10 }}>Cancel Edit</button>
            ) : null}
          </form>
        </div>

        <div className="admin-section">
          <h2>All Devices ({products.length})</h2>
          {products.length > 0 ? (
            <div className="products-table-container">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Image</th>
                    <th>Device Name</th>
                    <th>Type</th>
                    <th>Unit Price</th>
                    <th>Stock</th>
                    <th>Description</th>
                    <th>Created Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((product) => (
                    <tr key={product.id}>
                      <td className="product-image-cell">
                        <img src={product.image || "/static/images/product.jpg"} alt={product.name} className="product-thumb" />
                      </td>
                      <td><strong>{product.name}</strong></td>
                      <td>{product.type || "-"}</td>
                      <td>${Number(product.price).toFixed(2)}</td>
                      <td><span className={`stock-badge ${product.stock > 0 ? "in-stock" : "out-of-stock"}`}>{product.stock}</span></td>
                      <td>{product.description?.length > 50 ? `${product.description.slice(0, 50)}...` : product.description || "-"}</td>
                      <td>{product.created_at ? new Date(product.created_at).toLocaleDateString() : "-"}</td>
                      <td>
                        <button className="btn btn-sm btn-secondary" onClick={() => startEdit(product)}>Edit</button>
                        <button className="btn btn-sm btn-danger" onClick={() => handleDelete(product)} style={{ marginLeft: 8 }}>Delete</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p>No devices found.</p>
          )}
        </div>
      </div>
    </div>
  );
}
